<template>
  <section class="container">
    <div class="columns is-multiline">
      <div class="column is-10 is-offset-1 register">
        <div class="columns">
          <div class="column left has-text-centered mt-6">
            <img src="/img/main-logo.svg" />
            <p>
              {{ $t("auth.welcome1") }}<br />
              {{ $t("auth.welcome2") }}
            </p>
            <p class="mt-3">
              <a
                v-if="lang === 'en-US'"
                href="https://en.ryoden.co.jp/privacy"
                target="_blank"
                >{{ $t("auth.privacy") }}</a
              >
              <a
                v-if="lang === 'ja'"
                href="https://www.ryoden.co.jp/privacy"
                target="_blank"
                >{{ $t("auth.privacy") }}</a
              >
              {{ $t("auth.privacyAgree") }}
            </p>

            <p class="mt-6">
              <router-link :to="{ name: 'authSignIn' }">
                {{ $t("auth.alreadyMember") }}
              </router-link>
            </p>
            <p class="mt-4">
              <router-link :to="{ name: 'authConfirm' }">
                {{ $t("auth.alreadyCode") }}
              </router-link>
            </p>
          </div>
          <div class="column right">
            <h5 class="title is-5 has-text-centered">
              {{ $t("auth.createAccount") }}
            </h5>
            <form @submit.prevent="signUp">
              <div class="field">
                <label class="label">{{ $t("Space") }}</label>
                <div class="control">
                  <input
                    id="space"
                    class="input"
                    type="text"
                    placeholder="Your Space"
                    required
                    readonly
                    v-model="form.space"
                  />
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t("auth.email") }}</label>
                <div class="control">
                  <input
                    id="email"
                    class="input"
                    type="email"
                    placeholder="Your Email"
                    readonly
                    required
                    v-model="form.email"
                  />
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t("auth.name") }}</label>
                <div class="control">
                  <input
                    id="name"
                    class="input"
                    type="text"
                    placeholder="Your Name"
                    required
                    v-model="form.name"
                    :readonly="isInvite"
                    @input="changeName"
                  />
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t("Language") }}</label>
                <div class="select">
                  <select name="lang" v-model="form.lang">
                    <option value="ja">{{ $t("ja") }}</option>
                    <option value="en-US">{{ $t("en-US") }}</option>
                  </select>
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t("auth.password") }}</label>
                <div class="control">
                  <input
                    id="password"
                    class="input"
                    type="password"
                    placeholder="Your Password"
                    required
                    v-model="form.password"
                    :readonly="isInvite"
                    @input="changePassword"
                  />
                  <span class="has-text-danger" style="font-size:10pt">{{
                    $t("auth.passwordMessage")
                  }}</span>
                </div>
              </div>
              <button
                class="button is-block is-primary is-fullwidth is-medium"
                v-bind:class="{ 'is-loading': isLoad }"
                :disabled="isDisabled"
              >
                {{ $t("auth.signUp") }}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div class="column is-8 is-offset-2 has-text-centered mt-4">
        <AlertMessage color="is-danger" :message="errorMessage"></AlertMessage>
      </div>
    </div>
  </section>
</template>

<script>
import imsr from "@/imsr-api";
import { ref, reactive } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import AlertMessage from "@/components/AlertMessage.vue";
import { computed } from "vue";

export default {
  components: {
    AlertMessage,
  },
  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const form = reactive({
      email: route.query.email,
      password: "",
      name: "",
      space: route.query.space,
      lang: window.navigator.language,
    });
    const errorMessage = ref();
    const isLoad = ref(false);
    const isDisabled = ref(true);
    const isInvite = ref(true);
    const checkUserName = ref(false);
    const checkPassword = ref(false);
    const passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[a-zA-Z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{8,256}$/;

    const checkInvite = async () => {
      const invite_lang = route.query.lang ? route.query.lang : "ja";
      locale.value = invite_lang;
      store.commit("setLocale", invite_lang);
      const check = await imsr.checkInvite(
        form.email,
        form.space,
        route.query.code
      );
      if (check.status == "true") {
        isInvite.value = false;
      } else {
        errorMessage.value = t("auth.noInvaite");
      }
      isInvite.value = false;
    };

    checkInvite();

    const changeName = (event) => {
      if (event.target.value.length > 0) {
        checkUserName.value = true;
      } else {
        checkUserName.value = false;
      }
      checkSubmit();
    };
    const changePassword = (event) => {
      if (passwordPattern.test(event.target.value)) {
        checkPassword.value = true;
      } else {
        checkPassword.value = false;
      }
      checkSubmit();
    };

    const checkSubmit = () => {
      if (checkUserName.value && checkPassword.value) {
        isDisabled.value = false;
      } else {
        isDisabled.value = true;
      }
    };
    const signUp = async () => {
      try {
        isLoad.value = true;
        const params = {
          tenantId: form.space,
          email: form.email,
          password: form.password,
          userName: form.name,
          code: route.query.code,
          lang: form.lang,
        };
        const res = await imsr.createUser(params);
        if (res.status == "true") {
          store.commit("setUserId", form.email);
          router.push({ name: "authConfirm" });
        }
        if (res.message == "The user's invitation has expired") {
          errorMessage.value = t("auth.noInvaite");
        } else if (res.message == "User not found") {
          errorMessage.value = t("auth.noInvaite");
        } else if (res.message == "User exists") {
          errorMessage.value = t("auth.errorUserExist");
        } else if (res.message == "Code mismatch") {
          errorMessage.value = t("auth.errorCodeMismatch");
        }
        isLoad.value = false;
      } catch (e) {
        if (e.code == "InvalidPasswordException") {
          errorMessage.value = t("auth.errorPassword");
        } else {
          errorMessage.value = t("auth.errorRegistUser");
        }
        isLoad.value = false;
      }
    };

    return {
      form,
      errorMessage,
      isLoad,
      signUp,
      isDisabled,
      isInvite,
      changeName,
      changePassword,
      lang: computed(() => {
        return locale.value;
      }),
    };
  },
};
</script>

<style>
body {
  background: var(--background);
  height: 100vh;
  color: var(--textDark);
}

.logo {
  width: 200px;
}

footer {
  margin-top: 2.5rem;
}

.field:not(:last-child) {
  margin-bottom: 1rem;
}

.register {
  margin-top: 10rem;
  background: white;
  border-radius: 10px;
}

.left,
.right {
  padding: 4.5rem;
}

.left {
  border-right: 5px solid var(--background);
}

.left .title {
  font-weight: 800;
  letter-spacing: -2px;
}

.left .colored {
  color: var(--brandColor);
  font-weight: 500;
  margin-top: 1rem !important;
  letter-spacing: -1px;
}

.left p {
  color: var(--textLight);
  font-size: 1.15rem;
}

.right .title {
  font-weight: 800;
  letter-spacing: -1px;
}

.right .description {
  margin-top: 1rem;
  margin-bottom: 1rem !important;
  color: var(--textLight);
  font-size: 1.15rem;
}

.right small {
  color: var(--textLight);
}

input {
  font-size: 1rem;
}

input:focus {
  border-color: var(--brandColor) !important;
  box-shadow: 0 0 0 1px var(--brandColor) !important;
}

.fab,
.fas {
  color: var(--textLight);
  margin-right: 1rem;
}
</style>
